import React from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import styles from './styles.module.css';

export interface ErrorAction {
    label: string;
    onClick: () => void;
}

export interface ErrorPageProps {
    title?: string;
    message?: React.ReactNode;
    /** Primary call to action. Defaults to "Go to home page". */
    primaryAction?: ErrorAction;
    /** Optional secondary, outlined action. */
    secondaryAction?: ErrorAction;
    /** Optional small print under the buttons, e.g. a support contact. */
    support?: React.ReactNode;
    /**
     * @deprecated Legacy slot kept for older callers. Prefer `title`, `message`
     * and the action props so the layout stays consistent.
     */
    children?: React.ReactNode;
}

const DEFAULT_MESSAGE =
    'Our system is feeling a bit rebellious today. Please try again in a moment.';

const ErrorIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M7 7l10 10M17 7L7 17"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        />
    </svg>
);

/**
 * @deprecated Kept so existing imports keep compiling. Renders the message
 * only; the icon is now part of ErrorPage itself.
 */
export const DefaultErrorBody = ({ err }: { errIcon?: string, err?: string }) => {
    const location = useLocation();
    return <p className={styles.message}>{err || location.state?.message || DEFAULT_MESSAGE}</p>;
};

const ErrorPage: React.FC<ErrorPageProps> = ({
    title = 'Whoops!',
    message,
    primaryAction,
    secondaryAction,
    support,
    children,
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const primary: ErrorAction = primaryAction ?? {
        label: 'Go to home page',
        onClick: () => navigate(location.state?.from || '/'),
    };

    return (
        <main className={styles.page}>
            <section className={styles.card} role="alert" aria-live="polite">
                <div className={styles.icon}>
                    <ErrorIcon />
                </div>
                <h1 className={styles.title}>{title}</h1>
                {children ?? (
                    <p className={styles.message}>
                        {message || location.state?.message || DEFAULT_MESSAGE}
                    </p>
                )}
                <div className={styles.actions}>
                    <button type="button" className={`${styles.button} ${styles.primary}`} onClick={primary.onClick}>
                        {primary.label}
                    </button>
                    {secondaryAction && (
                        <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={secondaryAction.onClick}>
                            {secondaryAction.label}
                        </button>
                    )}
                </div>
                {support && <p className={styles.support}>{support}</p>}
            </section>
        </main>
    );
};

export default ErrorPage;
